import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, FileText, AlertCircle, Plus, Save } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  scheduled:          "bg-blue-50 text-blue-800 border-blue-200",
  in_progress:        "bg-amber-50 text-amber-800 border-amber-200",
  completed:          "bg-emerald-50 text-emerald-800 border-emerald-200",
  follow_up_required: "bg-amber-50 text-amber-800 border-amber-200",
  closed:             "bg-slate-100 text-slate-700 border-slate-200",
};

export default function SiteVisitReportPanel({ applicationId, reviewers = [], onChange }) {
  const { user } = useAuth();
  const isStaff = user.role === "admin" || user.role === "reviewer";
  const [visits, setVisits] = useState([]);
  const [findings, setFindings] = useState({}); // {site_visit_id: [findings]}
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ scheduled_date: "", visitor_user_ids: [], agenda: "" });
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState({ findings_summary: "", recommendations: "", compliance_score: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/site-visits", { params: { school_application_id: applicationId } });
      setVisits(r.data);
      // Load findings per visit
      const map = {};
      for (const v of r.data) {
        try {
          const fr = await api.get("/compliance-findings", { params: { site_visit_id: v.id } });
          map[v.id] = fr.data;
        } catch { /* skip */ }
      }
      setFindings(map);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [applicationId]);

  const schedule = async () => {
    if (!scheduleForm.scheduled_date) {
      toast.error("Visit date is required");
      return;
    }
    try {
      await api.post("/site-visits", { ...scheduleForm, school_application_id: applicationId });
      toast.success("Site visit scheduled");
      setShowSchedule(false);
      setScheduleForm({ scheduled_date: "", visitor_user_ids: [], agenda: "" });
      reload();
      onChange?.();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const openReport = (v) => {
    setSelected(v);
    setReport({
      findings_summary: v.findings_summary || "",
      recommendations: v.recommendations || "",
      compliance_score: v.compliance_score ?? "",
      notes: v.notes || "",
    });
  };

  const saveReport = async (status) => {
    setSaving(true);
    try {
      const payload = { ...report };
      if (payload.compliance_score === "") payload.compliance_score = null;
      else payload.compliance_score = Number(payload.compliance_score);
      if (status) payload.status = status;
      const r = await api.patch(`/site-visits/${selected.id}`, payload);
      toast.success(status ? `Visit marked ${status.replace(/_/g, " ")}` : "Report saved");
      setSelected(r.data);
      reload();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="site-visit-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-primary flex items-center gap-2">
          <Calendar className="h-4 w-4" /> Site visits ({visits.length})
        </h3>
        {isStaff && (
          <Button size="sm" variant="outline" onClick={() => setShowSchedule(true)} data-testid="sv-schedule-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Schedule visit
          </Button>
        )}
      </div>

      {visits.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 border-slate-200 text-sm" data-testid="sv-empty">
          No site visits yet.
        </Card>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => {
            const vf = findings[v.id] || [];
            const openCount = vf.filter((f) => !["corrected", "verified", "closed"].includes(f.resolution_status)).length;
            return (
              <Card key={v.id} className="p-4 border-slate-200" data-testid={`sv-row-${v.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className={`border capitalize text-[10px] ${STATUS_TONE[v.status] || ""}`}>
                        {v.status?.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-sm font-medium text-primary">
                        {v.scheduled_date || v.visit_date}
                      </span>
                      {v.compliance_score != null && (
                        <Badge variant="outline" className="text-[10px]">
                          {v.compliance_score}% compliance
                        </Badge>
                      )}
                    </div>
                    {v.agenda && <p className="text-xs text-slate-600">Agenda: {v.agenda}</p>}
                    {v.findings_summary && (
                      <p className="text-xs text-slate-700 mt-1 line-clamp-2"><strong>Findings:</strong> {v.findings_summary}</p>
                    )}
                    {/* Linked findings count */}
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                      <AlertCircle className="h-3 w-3" />
                      <span>{vf.length} findings recorded ({openCount} outstanding)</span>
                    </div>
                  </div>
                  {isStaff && (
                    <Button size="sm" variant="outline" onClick={() => openReport(v)} data-testid={`sv-report-${v.id}`}>
                      <FileText className="h-3.5 w-3.5 mr-1" /> Report
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Schedule dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="max-w-lg" data-testid="sv-schedule-dialog">
          <DialogHeader><DialogTitle>Schedule site visit</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Visit date</Label>
              <Input type="date" data-testid="sv-date" value={scheduleForm.scheduled_date} onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })} />
            </div>
            <div>
              <Label>Agenda (optional)</Label>
              <Textarea rows={2} data-testid="sv-agenda" value={scheduleForm.agenda} onChange={(e) => setScheduleForm({ ...scheduleForm, agenda: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedule(false)}>Cancel</Button>
            <Button onClick={schedule} className="bg-primary text-white hover:bg-[#113A64]" data-testid="sv-schedule-confirm">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl" data-testid="sv-report-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Site visit report
                  <Badge className={`border capitalize text-[10px] ${STATUS_TONE[selected.status] || ""}`}>{selected.status?.replace(/_/g, " ")}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Visit date</span><div className="text-slate-700">{selected.scheduled_date || selected.visit_date}</div></div>
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Reviewer</span><div className="text-slate-700">{(selected.visitor_user_ids || []).join(", ") || "—"}</div></div>
                <div><span className="uppercase tracking-[0.18em] text-slate-400">Linked findings</span><div className="text-slate-700">{(findings[selected.id] || []).length}</div></div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Findings summary</Label>
                  <Textarea rows={3} data-testid="sv-summary" value={report.findings_summary} onChange={(e) => setReport({ ...report, findings_summary: e.target.value })} />
                </div>
                <div>
                  <Label>Recommendations</Label>
                  <Textarea rows={3} data-testid="sv-recommendations" value={report.recommendations} onChange={(e) => setReport({ ...report, recommendations: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Compliance score (0-100)</Label>
                    <Input type="number" min="0" max="100" data-testid="sv-score" value={report.compliance_score} onChange={(e) => setReport({ ...report, compliance_score: e.target.value })} />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input data-testid="sv-notes" value={report.notes} onChange={(e) => setReport({ ...report, notes: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Linked findings (read-only list) */}
              {(findings[selected.id] || []).length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Linked compliance findings</div>
                  <div className="space-y-1.5 max-h-[200px] overflow-auto">
                    {findings[selected.id].map((f) => (
                      <div key={f.id} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded" data-testid={`sv-finding-${f.id}`}>
                        <Badge className="text-[10px] capitalize">{f.severity}</Badge>
                        <span className="font-medium text-primary">{f.title}</span>
                        <Badge variant="outline" className="ml-auto text-[10px] capitalize">{f.resolution_status?.replace(/_/g, " ")}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-4 flex-wrap gap-2">
                <Button variant="outline" onClick={() => saveReport(null)} disabled={saving} data-testid="sv-save">
                  <Save className="h-3.5 w-3.5 mr-1" /> Save report
                </Button>
                <Button variant="outline" onClick={() => saveReport("completed")} disabled={saving} data-testid="sv-complete">
                  Mark complete
                </Button>
                <Button variant="outline" onClick={() => saveReport("follow_up_required")} disabled={saving} data-testid="sv-followup">
                  Follow-up required
                </Button>
                <Button variant="outline" onClick={() => saveReport("closed")} disabled={saving} data-testid="sv-close">
                  Close visit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
